import { EXTENSION_POINT_ID_MAX_LENGTH, EXTENSION_POINT_ID_PATTERN, FIRST_PARTY_APPS, MAX_SHARED_DEPENDENCIES, MAX_SURFACES_PER_APP, MAX_SURFACE_OUTPUTS, MAX_SURFACE_POINT_REFS, MAX_SURFACE_USES, PERMISSION_KEY_PATTERN, SHARED_MODULE_SPECIFIER_MAX_LENGTH, SHARED_MODULE_SPECIFIER_PATTERN, SURFACE_KINDS, SURFACE_NAME_MAX_LENGTH, SURFACE_NAME_PATTERN, SURFACE_OUTPUT_NAME_MAX_LENGTH, SURFACE_OUTPUT_NAME_PATTERN, SURFACE_UI_PACKAGE_MAX_LENGTH, SURFACE_USE_APP_MAX_LENGTH, SURFACE_USE_APP_PATTERN, isRepresentable, mountPathSchema, mountPathSchemaForApp } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/surface-declaration.schemas.ts
/**
* The wire twin of `SurfaceDeclaration` (layered shell surfaces, R-1, R-20, R-35).
*
* An `app.json` `surfaces[]` block travels from the CP publish door to the DP inside the app
* manifest. These zod schemas let a receiver that holds the block as `unknown` parse it to the
* `SurfaceDeclaration` type of `@zion/sdk-contracts`, with the same per-field rules as
* `surface-manifest.schema.json`. Every object is `.strict()`, so an unknown key at any level is
* refused. A declared schema (`input`, an output, a `uses[]` shape, `settings`) must pass
* `isRepresentable`, the closed type language (D8), so the twin inherits every bound of that
* language: a free string without `maxLength` from 0 to 4096 fails (R-36, R-51), a `pattern` that
* is not a name from `SURFACE_PATTERNS` fails, a regular expression included (R-54), and an enum or
* tag `maxLength` must fit every listed value (R-52).
*
* The inferred type is the contract's `SurfaceDeclaration`; this module declares no type of its
* own, so a consumer imports the one type from `@zion/sdk-contracts`. Cross-field rules (one
* root, the fields a `kind` needs) are the publish rule modules', not these schemas'.
*
* Surface extension points add three optional point lists to an entry (`implements`, `fills`,
* `slots`), each at most `MAX_SURFACE_POINT_REFS` ids of the `EXTENSION_POINT_ID_PATTERN` grammar,
* and a second arm to a `uses[]` row: `{ point, app?, inputs?, outputs? }` beside
* `{ app, surface, inputs?, outputs? }`. Both arms are strict, so a row with `surface` and `point`
* fits neither, as the JSON Schema's `oneOf` refuses it.
*
* No `.transform()` appears here, so the schemas stay representable for `createOpenApiDocument()`.
*/
/**
* One declared schema node: any JSON object that passes `isRepresentable`. The refine is the one
* judgement of the type language; the twin states no bound of its own, so it cannot drift from
* the publish door's (R-51).
*/
const SurfaceTypeNodeSchema = z.record(z.string(), z.unknown()).refine(isRepresentable, { message: "the schema leaves the surface type language (D8)" }).readonly();
const surfaceNameSchema = z.string().max(SURFACE_NAME_MAX_LENGTH).regex(new RegExp(SURFACE_NAME_PATTERN));
/** Output schemas by output name, at most `MAX_SURFACE_OUTPUTS`. */
const SurfaceOutputsSchema = z.record(z.string().max(SURFACE_OUTPUT_NAME_MAX_LENGTH).regex(new RegExp(SURFACE_OUTPUT_NAME_PATTERN)), SurfaceTypeNodeSchema).refine((outputs) => Object.keys(outputs).length <= MAX_SURFACE_OUTPUTS, { message: `at most ${MAX_SURFACE_OUTPUTS} outputs` }).readonly();
const useAppSchema = z.string().max(SURFACE_USE_APP_MAX_LENGTH).regex(new RegExp(SURFACE_USE_APP_PATTERN));
/** One extension point id: at most `EXTENSION_POINT_ID_MAX_LENGTH` characters of `EXTENSION_POINT_ID_PATTERN`. Module-private. */
const extensionPointIdSchema = z.string().max(EXTENSION_POINT_ID_MAX_LENGTH).regex(new RegExp(EXTENSION_POINT_ID_PATTERN));
/** One point list of an entry, at most `MAX_SURFACE_POINT_REFS` ids. A repeated id is the rule module's to refuse. */
const extensionPointRefsSchema = z.array(extensionPointIdSchema).max(MAX_SURFACE_POINT_REFS).readonly();
/** The `uses[]` arm that names an app and an embedded surface. Module-private: the union below is the export. */
const surfaceAppUseSchema = z.object({
	app: useAppSchema,
	surface: surfaceNameSchema,
	inputs: SurfaceTypeNodeSchema.optional(),
	outputs: SurfaceOutputsSchema.optional()
}).strict().readonly();
/** The `uses[]` arm that names an extension point id, with an optional implementer app (D9). Module-private. */
const surfacePointUseSchema = z.object({
	point: extensionPointIdSchema,
	app: useAppSchema.optional(),
	inputs: SurfaceTypeNodeSchema.optional(),
	outputs: SurfaceOutputsSchema.optional()
}).strict().readonly();
/**
* One `uses[]` row: the app arm `{ app, surface, inputs?, outputs? }` or the point arm
* `{ point, app?, inputs?, outputs? }`, in that order. Each arm is strict, so a row fits at most one
* arm, and the union refuses what the JSON Schema `oneOf` refuses. Its `options` are the two arms.
*/
const SurfaceUseDeclarationSchema = z.union([surfaceAppUseSchema, surfacePointUseSchema]);
/**
* The strict object of one `surfaces[]` entry, before `.readonly()`. Module-private: the per-app
* variants extend it with their own `mountPath`, so the two forms share every other field rule.
*/
const surfaceDeclarationObject = z.object({
	name: surfaceNameSchema,
	kind: z.enum(SURFACE_KINDS),
	root: z.boolean().optional(),
	uiPackage: z.string().max(SURFACE_UI_PACKAGE_MAX_LENGTH).regex(/^@/),
	input: SurfaceTypeNodeSchema.optional(),
	outputs: SurfaceOutputsSchema.optional(),
	uses: z.array(SurfaceUseDeclarationSchema).max(MAX_SURFACE_USES).readonly().optional(),
	mountPath: mountPathSchema.optional(),
	defaultRoute: z.string().regex(/^\//).optional(),
	requiresPermission: z.string().regex(PERMISSION_KEY_PATTERN).optional(),
	sharedDependencies: z.array(z.string().max(SHARED_MODULE_SPECIFIER_MAX_LENGTH).regex(new RegExp(SHARED_MODULE_SPECIFIER_PATTERN))).max(MAX_SHARED_DEPENDENCIES).readonly().optional(),
	settings: SurfaceTypeNodeSchema.optional(),
	implements: extensionPointRefsSchema.optional(),
	fills: extensionPointRefsSchema.optional(),
	slots: extensionPointRefsSchema.optional()
}).strict();
/**
* One `surfaces[]` entry, under the general `mountPath` rule: `mountPathSchema` refuses every
* reserved first segment (#2146), `/admin` included. Use {@link surfaceDeclarationSchemaForApp}
* where the app slug is known.
*/
const SurfaceDeclarationSchema = surfaceDeclarationObject.readonly();
/** A whole `surfaces[]` block, at most `MAX_SURFACES_PER_APP` entries. */
const SurfaceDeclarationsSchema = z.array(SurfaceDeclarationSchema).max(MAX_SURFACES_PER_APP).readonly();
function firstPartyDeclarationSchema(appId) {
	return surfaceDeclarationObject.extend({ mountPath: mountPathSchemaForApp(appId).optional() }).readonly();
}
function firstPartyDeclarationsSchema(entry) {
	return z.array(entry).max(MAX_SURFACES_PER_APP).readonly();
}
/**
* One parser pair per first-party app, built once at module load from `FIRST_PARTY_APPS`. The list
* is code, so the map is bounded; no caller input adds an entry.
*/
const firstPartySchemas = new Map(FIRST_PARTY_APPS.map((row) => {
	const entry = firstPartyDeclarationSchema(row.appId);
	return [row.appId, {
		entry,
		block: firstPartyDeclarationsSchema(entry)
	}];
}));
/**
* One `surfaces[]` entry for the app with slug `appId` (#3673, admin-redesign-asks C-2, D-10).
*
* Callers: core-api `surfacesOf` and the shell `projectSurfaces`, each with the slug of the row the
* block belongs to (the registered app slug, never the manifest's own `appId` field).
*
* - A first-party `appId` (a row of `FIRST_PARTY_APPS`): every field rule of
*   {@link SurfaceDeclarationSchema}, strict, with `mountPath: mountPathSchemaForApp(appId)`. So
*   `mountPath` admits the app's one listed mount path (`/admin` for `admin-app`), which may begin
*   with a reserved segment, and refuses every other value. An absent `mountPath` passes, as in
*   the general rule.
* - Every other `appId`, the empty string included: {@link SurfaceDeclarationSchema} itself, by
*   identity, so it parses exactly as the general schema does and a third-party row at `/admin`
*   stays refused.
*
* Pure: no I/O, no state. The parser for a first-party app is built once at module load.
*/
function surfaceDeclarationSchemaForApp(appId) {
	return firstPartySchemas.get(appId)?.entry ?? SurfaceDeclarationSchema;
}
/**
* A whole `surfaces[]` block for the app with slug `appId`: at most `MAX_SURFACES_PER_APP` entries,
* each parsed by {@link surfaceDeclarationSchemaForApp}. For an `appId` outside `FIRST_PARTY_APPS`
* it is {@link SurfaceDeclarationsSchema} itself, by identity. One refused entry refuses the block,
* as the general schema does. Pure.
*/
function surfaceDeclarationsSchemaForApp(appId) {
	return firstPartySchemas.get(appId)?.block ?? SurfaceDeclarationsSchema;
}
//#endregion
export { SurfaceDeclarationSchema, SurfaceDeclarationsSchema, SurfaceOutputsSchema, SurfaceTypeNodeSchema, SurfaceUseDeclarationSchema, surfaceDeclarationSchemaForApp, surfaceDeclarationsSchemaForApp };

//# sourceMappingURL=surface-declaration.schemas.js.map